import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useContext } from 'react';
import * as THREE from 'three';

import { DesignContext } from '../contexts/designContext.js';

const Tree = ({
  index = -1,
  position,
  height,
  radius,
  trunkHeight = null,
  treeType,
  addSelectedThree = null,
  removeSelectedThree = null,
  selectable = false,
  isSelected = false,
  visible = true,
  onTreeClick = null // Optional. When provided (e.g. by designer), called when this tree is clicked; used for 3D tree selection. Not used by other consumers.
}) => {
  const { showSunLight } = useContext(DesignContext);
  const [selected, setSelected] = useState(false);

  const [matTrunk, matBranch] = useMemo(() => {
    const mTrunk = <meshStandardMaterial attach="material" color="#732413" wireframe={false} side={THREE.FrontSide} />;

    let mBranch = null;
    if (showSunLight) {
      mBranch =
        selected && selectable ? (
          <meshStandardMaterial attach="material" color="#46de26" wireframe={false} side={THREE.FrontSide} />
        ) : (
          <meshStandardMaterial attach="material" color="#267615" wireframe={false} side={THREE.FrontSide} />
        );
    } else {
      mBranch =
        selected && selectable ? (
          <meshStandardMaterial attach="material" color="#46de26" wireframe={false} side={THREE.FrontSide} />
        ) : (
          <meshStandardMaterial attach="material" color="#267615" wireframe={false} side={THREE.FrontSide} />
        );
    }

    return [mTrunk, mBranch];
  }, [selected, selectable, showSunLight]);

  useEffect(() => {
    if (selectable && isSelected !== selected) setSelected(isSelected);
  }, [isSelected, index, selectable, selected]);

  const changeSelection = (e, selection) => {
    if (onTreeClick) {
      e.stopPropagation();
      onTreeClick();
      return;
    }
    if (selectable) {
      e.stopPropagation();
      setSelected(selection);
      const [pIndex, vIndex] = index;
      if (selection) addSelectedThree(pIndex, vIndex);
      else removeSelectedThree(pIndex, vIndex);
    }
  };

  const truncH = (() => {
    if (Number.isFinite(trunkHeight)) {
      return Math.min(Math.max(trunkHeight, 0), height);
    }
    return Math.max(0, height - 2 * radius);
  })();
  const canopyHeight = Math.max(height - truncH, 0);
  const [x, y, z] = position;
  const spherePosition = [x, truncH + canopyHeight / 2, y];
  const spherescaleY = radius ? canopyHeight / (radius * 2) : 1;

  return (
    <group name="tree" index={index} castShadow receiveShadow visible={height > 0}>
      <mesh
        name="treeTrunk"
        visible={visible}
        castShadow
        receiveShadow
        position={[x, z + truncH / 2, y]}
        onClick={(e) => changeSelection(e, !selected)}
      >
        <cylinderBufferGeometry attach="geometry" args={[radius / 7, radius / 7, truncH, 5]} />
        {matTrunk}
      </mesh>
      {treeType === 'Cone' ? (
        <mesh
          name="treeBranch"
          visible={visible}
          castShadow
          receiveShadow
          position={spherePosition}
          scale={[1, spherescaleY, 1]}
          onClick={(e) => changeSelection(e, !selected)}
        >
          <cylinderBufferGeometry attach="geometry" args={[0, radius, radius * 2, 32]} />
          {matBranch}
        </mesh>
      ) : (
        <mesh
          name="treeBranch"
          visible={visible}
          castShadow
          receiveShadow
          position={spherePosition}
          scale={[1, spherescaleY, 1]}
          onClick={(e) => changeSelection(e, !selected)}
        >
          <sphereGeometry attach="geometry" args={[radius, 32, 32]} />
          {matBranch}
        </mesh>
      )}
    </group>
  );
};

export default Tree;
